import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Switch,
  Badge,
  FormControlLabel,
} from '@mui/material';
import {
  ImageOutlined as PictureIcon,
  Visibility as PreviewIcon,
  Upload as UploadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  Button as RButton,
  Card as RCard,
  CardHeader as RCardHeader,
  Collapse as RCollapse,
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { getPicturesByUnitNo, getPictureVersionForPreview } from './api';
import { UploadPicture } from './UploadPicture';

const PICTURE_TYPES = [
  { id: 'living_room', picture_type: 'Living Room', title: 'Living Room', description: 'Pictures of the living room area', alwaysVisible: true },
  { id: 'kitchen', picture_type: 'Kitchen', title: 'Kitchen', description: 'Pictures of the kitchen area', alwaysVisible: true },
  { id: 'bedroom', picture_type: 'Bedroom', title: 'Bedroom', description: 'Pictures of the bedroom(s)', alwaysVisible: true },
  { id: 'bathroom', picture_type: 'Bathroom', title: 'Bathroom', description: 'Pictures of the bathroom(s)', alwaysVisible: true },
  { id: 'entrance', picture_type: 'Entrance', title: 'Entrance', description: 'Pictures of the main entry to the unit/property', alwaysVisible: true },
  { id: 'exterior', picture_type: 'Exterior', title: 'Exterior', description: 'Pictures of the property exterior', alwaysVisible: true },
  { id: 'balcony', picture_type: 'Balcony', title: 'Balcony', description: 'Pictures of the balcony or patio', alwaysVisible: false },
  { id: 'storage', picture_type: 'Storage', title: 'Storage', description: 'Pictures of storage areas like closets or sheds', alwaysVisible: false },
  { id: 'garage', picture_type: 'Garage', title: 'Garage', description: 'Pictures of the garage or parking area', alwaysVisible: false },
  { id: 'common_area', picture_type: 'Common Area', title: 'Common Area', description: 'Pictures of shared building areas', alwaysVisible: false },
  { id: 'damage_report', picture_type: 'Damage Report', title: 'Damage Report', description: 'Pictures documenting property damage', alwaysVisible: false },
];

const StaticPictureCard = ({ pictureType, pictures, onPreview, onUpload }) => {
  const hasPictures = pictures && pictures.length > 0;
  const pictureCount = pictures[0]?.previous_versions?.length ? pictures[0].previous_versions.length + 1 : 0;
  return (
    <Grid item xs={12} sm={6} md={6} key={pictureType.id}>
      <Card
        variant="outlined"
        sx={{
          border: 2,
          borderColor: hasPictures ? 'success.main' : 'error.main',
          backgroundColor: hasPictures ? 'rgb(225 255 227)' : 'rgb(255 233 233)',
          '&:hover': {
            boxShadow: 3,
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.3s ease',
          minWidth: 150,
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
              {hasPictures ? (
                <CheckIcon sx={{ color: 'success.main', mr: 1.5, fontSize: 20 }} />
              ) : (
                <ErrorIcon sx={{ color: 'error.main', mr: 1.5, fontSize: 20 }} />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  noWrap
                  title={pictureType.title}
                >
                  {pictureType.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {pictureType.description}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1 }}>
              <Tooltip title={`Preview ${pictureType.title} Pictures`}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onPreview(pictureType, pictures)}
                  sx={{
                    border: 1,
                    borderColor: 'primary.main',
                    p: 0.5,
                    '&:hover': { backgroundColor: 'primary.light' },
                  }}
                >
                  <Badge
                    badgeContent={pictureCount}
                    color={hasPictures ? 'success' : 'default'}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.6rem',
                        minWidth: 16,
                        height: 16,
                        padding: '0 4px',
                      },
                    }}
                  >
                    <PreviewIcon fontSize="small" />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Tooltip title={`Upload ${pictureType.title}`}>
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={() => onUpload(pictureType)}
                  sx={{
                    border: 1,
                    borderColor: 'secondary.main',
                    p: 0.5,
                    '&:hover': { backgroundColor: 'secondary.light' },
                  }}
                >
                  <UploadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

const PictureTypePreviewDialog = ({ open, onClose, pictureType, pictures, onUpload, propertyId, buildingName, unitNo }) => {
  const [selectedPicture, setSelectedPicture] = useState(null);
  const [showLatest, setShowLatest] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    if (open && pictures && pictures.length > 0) {
      // Always start with the latest picture
      setSelectedPicture(pictures[0]);
      setShowLatest(true);
      setPreviewError(null);
    } else {
      setSelectedPicture(null);
      setShowLatest(true);
      setPreviewError(null);
    }
  }, [open, pictures]);

  const handlePictureSelect = async (picture, isLatest = false) => {
    setLoadingPreview(true);
    setPreviewError(null);

    try {
      // If it's the latest picture, it should already have signedUrl
      if (isLatest || !picture.version_number) {
        // For latest picture, use existing signedUrl
        if (picture.signedUrl && picture.signedUrl !== 'upload_requested') {
          setSelectedPicture(picture);
          setShowLatest(isLatest);
        } else {
          throw new Error('Latest picture preview not available');
        }
      } else {
        // For previous versions, fetch the signed URL
        const versionData = await getPictureVersionForPreview(
          propertyId,
          buildingName,
          unitNo,
          pictureType.picture_type,
          picture.version_number
        );

        if (versionData && versionData.signedUrl) {
          setSelectedPicture({
            ...picture,
            signedUrl: versionData.signedUrl,
          });
          setShowLatest(false);
        } else {
          throw new Error('Failed to get preview URL for this version');
        }
      }
    } catch (error) {
      console.error('Error fetching picture version for preview:', error);
      setPreviewError('Failed to load picture preview. Please try again.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownload = async (picture) => {
    if (typeof window === 'undefined') return;

    try {
      let downloadUrl = picture.signedUrl;
      let fileName = picture.picture_type || `${pictureType?.title}_${picture.id || picture.version_number}`;

      // If it's a previous version without signedUrl, fetch it
      if (picture.version_number && picture.version_number > 1 && !picture.signedUrl) {
        const versionData = await getPictureVersionForPreview(
          propertyId,
          buildingName,
          unitNo,
          pictureType.picture_type,
          picture.version_number
        );

        if (versionData && versionData.signedUrl) {
          downloadUrl = versionData.signedUrl;
        } else {
          throw new Error('Failed to get download URL for this version');
        }
      }

      if (downloadUrl && downloadUrl !== 'upload_requested') {
        const link = window.document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
      } else {
        throw new Error('Picture not available for download');
      }
    } catch (err) {
      console.error(`Error downloading picture ${picture.picture_type}:`, err);
      Swal.fire('Error', 'Failed to download picture. Please try again.', 'error');
    }
  };

  const handleDownloadAll = async () => {
    try {
      // Download latest version
      if (pictures[0] && pictures[0].url !== 'upload_requested' && pictures[0].signedUrl) {
        await handleDownload(pictures[0]);
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      // Download previous versions
      if (pictures[0]?.previous_versions) {
        for (const version of pictures[0].previous_versions) {
          await handleDownload(version);
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
    } catch (error) {
      console.error('Error downloading all pictures:', error);
      Swal.fire('Error', 'Some pictures could not be downloaded. Please try again.', 'error');
    }
  };

  const handleUploadClick = () => {
    onUpload(pictureType);
    onClose();
  };

  const handleToggleLatest = () => {
    if (pictures && pictures.length > 0) {
      // When toggling to show latest, select the first picture (latest)
      if (!showLatest) {
        handlePictureSelect(pictures[0], true);
      } else {
        // When toggling off latest, just update the state
        setShowLatest(false);
      }
    }
  };

  // Function to handle clicking on latest picture card
  const handleLatestPictureClick = () => {
    if (pictures && pictures.length > 0) {
      handlePictureSelect(pictures[0], true);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            {pictureType?.title || 'Pictures'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {pictures && pictures.length > 0 && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<FeatherIcon icon="download" size="16" />}
              onClick={handleDownloadAll}
            >
              Download All
            </Button>
          )}
          <Button
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<FeatherIcon icon="upload" size="16" />}
            onClick={handleUploadClick}
          >
            Upload New
          </Button>
          <IconButton onClick={onClose} size="small">
            <FeatherIcon icon="x" size="16" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {pictures && pictures.length > 0 ? (
          <Box sx={{ height: '70vh', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {loadingPreview ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                  <CircularProgress />
                  <Typography variant="body2" ml={2}>Loading preview...</Typography>
                </Box>
              ) : previewError ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                  <Alert severity="error">{previewError}</Alert>
                </Box>
              ) : selectedPicture && selectedPicture.signedUrl && selectedPicture.signedUrl !== 'upload_requested' ? (
                <img
                  src={selectedPicture.signedUrl}
                  alt={selectedPicture.picture_type}
                  style={{ width: '100%', height: '60vh', objectFit: 'contain' }}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                  <Typography color="text.secondary">Picture preview not available.</Typography>
                </Box>
              )}
            </Box>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Picture Versions
                </Typography>
                <FormControlLabel
                  control={<Switch checked={showLatest} onChange={handleToggleLatest} size="small" />}
                  label="Show Latest"
                  labelPlacement="start"
                  sx={{ m: 0 }}
                />
              </Box>
              <Grid container spacing={1}>
                {/* Latest Picture Card */}
                {pictures[0] && (
                  <Grid item xs={6} sm={6} md={4}>
                    <Card
                      variant="outlined"
                      sx={{
                        border: 2,
                        borderColor: pictures[0].url !== 'upload_requested' ? 'success.main' : 'error.main',
                        backgroundColor: selectedPicture === pictures[0] && showLatest
                          ? 'primary.light'
                          : pictures[0].url !== 'upload_requested'
                            ? 'rgb(225 255 227)'
                            : 'rgb(255 233 233)',
                        '&:hover': {
                          boxShadow: 3,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                        minWidth: 120,
                        cursor: 'pointer',
                      }}
                      onClick={handleLatestPictureClick}
                    >
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
                            {pictures[0].url !== 'upload_requested' ? (
                              <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 18 }} />
                            ) : (
                              <PictureIcon sx={{ color: 'error.main', mr: 1, fontSize: 18 }} />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                fontWeight="bold"
                                noWrap
                                title="Latest Version"
                              >
                                Latest
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Comments: {pictures[0].comments ? pictures[0].comments : 'N/A'}
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Uploaded: {pictures[0].created_at ? new Date(pictures[0].created_at).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Preview Picture">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePictureSelect(pictures[0], true);
                                }}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Picture">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(pictures[0]);
                                }}
                                disabled={pictures[0].url === 'upload_requested'}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <FeatherIcon icon="download" size="12" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Previous Versions */}
                {pictures[0]?.previous_versions?.map((version) => (
                  <Grid item xs={6} sm={6} md={4} key={version.version_number}>
                    <Card
                      variant="outlined"
                      sx={{
                        border: 2,
                        borderColor: version.url !== 'upload_requested' ? 'success.main' : 'error.main',
                        backgroundColor: selectedPicture?.version_number === version.version_number && !showLatest
                          ? 'primary.light'
                          : version.url !== 'upload_requested'
                            ? 'rgb(225 255 227)'
                            : 'rgb(255 233 233)',
                        '&:hover': {
                          boxShadow: 3,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                        minWidth: 120,
                        cursor: 'pointer',
                      }}
                      onClick={() => handlePictureSelect(version, false)}
                    >
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
                            {version.url !== 'upload_requested' ? (
                              <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 18 }} />
                            ) : (
                              <PictureIcon sx={{ color: 'error.main', mr: 1, fontSize: 18 }} />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Tooltip title={version.comments || 'No comment available'}>
                                <Typography
                                  variant="caption"
                                  fontWeight="bold"
                                  noWrap
                                  title={`Version ${version.version_number}`}
                                >
                                  V{version.version_number}
                                </Typography>
                              </Tooltip>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Comments: {version.comments ? version.comments : 'N/A'}
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Uploaded: {version.created_at ? new Date(version.created_at).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Preview Picture">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePictureSelect(version, false);
                                }}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Picture">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(version);
                                }}
                                disabled={version.url === 'upload_requested'}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <FeatherIcon icon="download" size="12" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4, height: '70vh' }}>
            <PictureIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No {pictureType?.title} pictures found
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Upload pictures to get started
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FeatherIcon icon="upload" size="16" />}
              onClick={handleUploadClick}
            >
              Upload First Picture
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const Pictures = ({ propertyId, buildingName, unitNo, fetchPictures, onUpload }) => {
  const [pictures, setPictures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedPictureType, setSelectedPictureType] = useState(null);
  const [selectedPictures, setSelectedPictures] = useState([]);

  const fetchPics = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!propertyId || !buildingName || !unitNo) {
        console.warn('Missing required parameters for picture fetch', { propertyId, buildingName, unitNo });
        setPictures([]);
        return;
      }

      const response = await getPicturesByUnitNo(propertyId, buildingName, unitNo);
      if (!response || !response.success) {
        throw new Error(response?.message || 'Failed to fetch pictures');
      }

      setPictures(response.data.pictures || []);
    } catch (err) {
      setPictures([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPics();
  }, [propertyId, buildingName, unitNo]);

  useEffect(() => {
    if (fetchPictures) {
      fetchPictures.current = fetchPics;
    }
  }, [fetchPictures]);

  const getPicturesByType = (typeId) => {
    return pictures.filter(pic => pic.picture_type === PICTURE_TYPES.find(pt => pt.id === typeId)?.picture_type);
  };

  const handlePreviewOpen = (pictureType, picturesOfType) => {
    setSelectedPictureType(pictureType);
    setSelectedPictures(picturesOfType || []);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setSelectedPictureType(null);
    setSelectedPictures([]);
  };

  const handleUpload = (pictureType) => {
    if (onUpload) {
      onUpload(pictureType);
    }
    setPreviewOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
        <Typography variant="body1" ml={2}>
          Loading pictures...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="warning" sx={{ my: 2 }}>{error}</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={2} sx={{ mt: 0, pt: 0 }}>
        {PICTURE_TYPES.map((pictureType) => {
          const picturesOfType = getPicturesByType(pictureType.id);
          const hasPictures = picturesOfType && picturesOfType.length > 0;

          if (pictureType.alwaysVisible || hasPictures) {
            return (
              <StaticPictureCard
                key={pictureType.id}
                pictureType={pictureType}
                pictures={picturesOfType}
                onPreview={handlePreviewOpen}
                onUpload={handleUpload}
              />
            );
          }
          return null;
        })}
      </Grid>
      <PictureTypePreviewDialog
        open={previewOpen}
        onClose={handlePreviewClose}
        pictureType={selectedPictureType}
        pictures={selectedPictures}
        onUpload={handleUpload}
        propertyId={propertyId}
        buildingName={buildingName}
        unitNo={unitNo}
      />
    </Box>
  );
};

const PicturesWrapper = ({ propertyId, buildingName, unitNo }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedPictureType, setSelectedPictureType] = useState(null);
  const fetchPicturesRef = useRef(null);

  const hasRequiredProps = Boolean(propertyId && buildingName && unitNo);

  const toggleCollapse = () => setIsOpen((prev) => !prev);

  const handleAddPicture = (pictureType = null) => {
    setSelectedPictureType(pictureType);
    setUploadDialogOpen(true);
  };

  const handleModalClose = () => {
    setUploadDialogOpen(false);
    setSelectedPictureType(null);
    if (fetchPicturesRef.current) {
      fetchPicturesRef.current();
    }
  };

  const handlePictureUploaded = () => {
    if (fetchPicturesRef.current) {
      fetchPicturesRef.current();
    }
  };

  const handleDownloadAll = async (pictureType = null, isPreviewOpen = false) => {
    if (!pictureType && isPreviewOpen) {
      Swal.fire('Warning', 'Please close the preview or select a specific picture type to download.', 'warning');
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to download ${pictureType ? pictureType.picture_type + ' pictures' : 'all pictures'}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, download',
      cancelButtonText: 'No, cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await getPicturesByUnitNo(propertyId, buildingName, unitNo);
          if (!response || !response.success) {
            throw new Error(response?.message || 'Failed to fetch pictures');
          }

          let pictures = response.data.pictures || [];
          if (pictures.length === 0) {
            Swal.fire('No Pictures', 'There are no pictures to download.', 'info');
            return;
          }

          if (pictureType) {
            pictures = pictures.filter(pic => pic.picture_type === pictureType.picture_type);
            if (pictures.length === 0) {
              Swal.fire('No Pictures', `No ${pictureType.picture_type} pictures found to download.`, 'info');
              return;
            }
          }

          let downloadCount = 0;
          for (const pic of pictures) {
            if (pic.url !== 'upload_requested' && pic.signedUrl) {
              try {
                const link = document.createElement('a');
                link.href = pic.signedUrl;
                link.download = pic.picture_type || `picture_${pic.id}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                downloadCount++;
                await new Promise((resolve) => setTimeout(resolve, 1000));
              } catch (err) {
                console.error(`Error downloading picture ${pic.picture_type}:`, err);
                Swal.fire('Error', `Failed to download ${pic.picture_type}.`, 'error');
              }
            }
            // Handle previous versions - fetch signed URLs as needed
            if (pic.previous_versions) {
              for (const version of pic.previous_versions) {
                if (version.url !== 'upload_requested') {
                  try {
                    let downloadUrl = version.signedUrl;

                    // If no signed URL, fetch it
                    if (!downloadUrl) {
                      const versionData = await getPictureVersionForPreview(
                        propertyId,
                        buildingName,
                        unitNo,
                        pic.picture_type,
                        version.version_number
                      );
                      downloadUrl = versionData?.signedUrl;
                    }

                    if (downloadUrl) {
                      const link = document.createElement('a');
                      link.href = downloadUrl;
                      link.download = version.picture_type || `picture_${version.version_number}`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      downloadCount++;
                      await new Promise((resolve) => setTimeout(resolve, 1000));
                    }
                  } catch (err) {
                    console.error(`Error downloading version ${version.version_number}:`, err);
                    Swal.fire('Error', `Failed to download version ${version.version_number}.`, 'error');
                  }
                }
              }
            }
          }

          if (downloadCount === 0) {
            Swal.fire('No Pictures', 'No valid pictures were available for download.', 'info');
          } else {
            Swal.fire('Success', `${downloadCount} picture${downloadCount > 1 ? 's' : ''} downloaded successfully.`, 'success');
          }
        } catch (err) {
          console.error('Error fetching pictures for download:', err);
          Swal.fire('Error', 'Failed to fetch pictures for download.', 'error');
        }
      }
    });
  };

  if (!hasRequiredProps) {
    return (
      <RCard className="mb-4">
        <RCardHeader className="d-flex justify-content-between align-items-center">
          <div className="p-0">
            Pictures
            <Typography variant="caption" component="div">
              Complete the form with property, building, and unit information to view pictures.
            </Typography>
          </div>
        </RCardHeader>
      </RCard>
    );
  }

  return (
    <RCard className="mb-4">
      <RCardHeader className="py-3 text-center">
        <RButton
          color="primary"
          outline
          className="fw-bold px-4 py-2 rounded-pill"
          onClick={toggleCollapse}
        >
          <FeatherIcon icon="image" className="me-2" size={16} />
          Pictures ({PICTURE_TYPES.filter(pt => pt.alwaysVisible).length})
        </RButton>
      </RCardHeader>
      <RCollapse isOpen={isOpen}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: "center", marginTop: '15px', gap: 2 }}>
            <RButton
              color="secondary"
              className="d-flex align-items-center gap-1"
              style={{ fontSize: '0.875rem', padding: '4px 12px', borderRadius: '20px' }}
              onClick={() => handleDownloadAll(null, false)}
            >
              <FeatherIcon icon="download" size="16" />
              Download All
            </RButton>
            <RButton
              color="primary"
              className="d-flex align-items-center gap-1"
              style={{ fontSize: '0.875rem', padding: '4px 12px', borderRadius: '20px' }}
              onClick={() => handleAddPicture()}
            >
              <FeatherIcon icon="plus" size="16" />
              Add Picture
            </RButton>
          </Box>
          <Pictures
            propertyId={propertyId}
            buildingName={buildingName}
            unitNo={unitNo}
            fetchPictures={fetchPicturesRef}
            onUpload={handleAddPicture}
          />
        </CardContent>
      </RCollapse>
      <UploadPicture
        isOpen={uploadDialogOpen}
        setOpen={setUploadDialogOpen}
        onClose={handleModalClose}
        onPictureUploaded={handlePictureUploaded}
        propertyId={propertyId}
        buildingName={buildingName}
        unitNo={unitNo}
        pictureType={selectedPictureType?.picture_type || null}
        pictures={PICTURE_TYPES}
      />
    </RCard>
  );
};

export { PicturesWrapper as Pictures };